import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Dropdown } from '@/components/Global'
import { DropdownType } from '@/enums'
import { AssignmentType } from "../AssessmentModal/AssessmentModal";
import './TestingWindow.scss';
import { AssessmentWindow } from '@/features/dashboard/utils/findAssessmentWindow';
import { useAssessmentPeriodWindows } from "@/hooks/useAssessmentPeriods";
import { AssessmentPeriodItem } from '@/hooks/useAssessmentPeriods';
import { AssessmentPeriodWindowType } from '@/hooks/useAssessmentPeriods';

export interface SelectedPeriodOption {
  label: string;
  value: string;
  startDate: string;
  endDate: string;
  originalPeriodId: number;
  screeningWindowType: string;
  assignmentId: string | null;
  assignmentType: AssignmentType | null;
}

export interface SelectedPeriodCallback {
  label: string;
  value: number;
  startDate: string;
  endDate: string;
  screeningWindowType: string;
  assignmentId: string | null;
  assignmentType: AssignmentType | null;
}

export interface TestingWindowProps {
  assessmentModal: boolean;
  assessmentWindowStatus?: AssessmentWindow;
  onSelectedPeriodChange?: (selectedOption: SelectedPeriodCallback) => void;
  currentDistrictWindow?: AssessmentPeriodItem;
  controlledSelectedWindow?: { startDate: string | null, endDate: string | null, value: number | null, screeningWindowType: string | null, assignmentId: string | null };
}

export const formatPeriodDateWithTags = (startDate: string, endDate: string, assignmentType: string, current?: boolean, screeningWindowType?: string, tags?: string[], outOfDistrictWindow?: boolean) => {
  const formattedName = assignmentType
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  const currentTag = (current && (screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT || (screeningWindowType === AssessmentPeriodWindowType.PERIOD && outOfDistrictWindow))) ? '(current)' : '';
  const formattedDate = screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT ? `(${startDate.slice(5, 7)}/${startDate.slice(8, 10)} - ${endDate.slice(5, 7)}/${endDate.slice(8, 10)})` : ``;
  const tagPrefix = tags && (tags[0] === 'BOY' || tags[0] === 'MOY' || tags[0] === 'EOY') ? `${tags?.join(', ')}` : '';
  const typePrefix = screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT ? 'District Assessment:' : ''; // unknown on exact ui preference
  return `${tagPrefix} ${formattedName} ${formattedDate} ${currentTag}`.trim();
};

const TestingWindow = ({ assessmentModal, onSelectedPeriodChange, currentDistrictWindow, controlledSelectedWindow, assessmentWindowStatus }: TestingWindowProps) => {
  const { loading, assessmentPeriodWindows } = useAssessmentPeriodWindows();
  const outOfDistrictWindow = !assessmentWindowStatus?.activeAssessmentWindow;
  const districtScreeningSetup = !!assessmentPeriodWindows.find((window: AssessmentPeriodItem) => window.screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT);


  const mapAssessmentPeriodsToOptions = useCallback((assessmentPeriods: AssessmentPeriodItem[]) => {
    return assessmentPeriods.map(period => ({
      label: formatPeriodDateWithTags(period.startDate, period.endDate, period.assignmentType, period.current, period.screeningWindowType || undefined, period.tags || undefined, outOfDistrictWindow),
      value: `${period.periodId}-${period.assignmentType}`,
      startDate: period.startDate,
      endDate: period.endDate,
      originalPeriodId: period.periodId,
      screeningWindowType: period.screeningWindowType,
      assignmentId: period.assignmentId,
      assignmentType: period.assignmentType as AssignmentType
    }));
  }, [outOfDistrictWindow]);

  const options = useMemo(() => mapAssessmentPeriodsToOptions(assessmentPeriodWindows), [assessmentPeriodWindows, mapAssessmentPeriodsToOptions]);

  const [selectedPeriod, setSelectedPeriod] = useState<SelectedPeriodOption | null>(null);

  const getTargetPeriod = useCallback(() => {
    if (loading || !assessmentPeriodWindows?.length) {
      return selectedPeriod;
    }

    if (controlledSelectedWindow?.value !== null && controlledSelectedWindow?.screeningWindowType) {
      return options.find(option =>
        option.originalPeriodId === controlledSelectedWindow.value &&
        option.screeningWindowType === controlledSelectedWindow.screeningWindowType
      ) || selectedPeriod;
    }

    if (currentDistrictWindow) {
      return options.find(option =>
        option.originalPeriodId === currentDistrictWindow.periodId &&
        option.screeningWindowType === currentDistrictWindow.screeningWindowType
      ) || selectedPeriod;
    }

    if ((outOfDistrictWindow || !districtScreeningSetup) && options.length > 0) {
      return options.find(option =>
        option.originalPeriodId === assessmentWindowStatus?.currentPeriodIdentifier &&
        option.screeningWindowType === AssessmentPeriodWindowType.PERIOD
      ) || selectedPeriod;
    }

    return selectedPeriod;
  }, [loading, assessmentPeriodWindows, controlledSelectedWindow, currentDistrictWindow, outOfDistrictWindow, districtScreeningSetup, options, assessmentWindowStatus, selectedPeriod]);

  useEffect(() => {
    const targetPeriod = getTargetPeriod();

    if (JSON.stringify(selectedPeriod) !== JSON.stringify(targetPeriod)) {
      setSelectedPeriod(targetPeriod as SelectedPeriodOption);
      if (targetPeriod && (outOfDistrictWindow || !districtScreeningSetup) && !controlledSelectedWindow?.value && onSelectedPeriodChange) {
        onSelectedPeriodChange({
          label: targetPeriod.label,
          value: assessmentWindowStatus?.currentPeriodIdentifier!,
          startDate: targetPeriod.startDate,
          endDate: targetPeriod.endDate,
          screeningWindowType: targetPeriod.screeningWindowType,
          assignmentId: targetPeriod.assignmentId,
          assignmentType: targetPeriod.assignmentType as AssignmentType
        });
      }
    }
  }, [getTargetPeriod, selectedPeriod, outOfDistrictWindow, districtScreeningSetup, controlledSelectedWindow, onSelectedPeriodChange, assessmentWindowStatus]);

  const handleDropdownChange = useCallback((selectedOption: SelectedPeriodOption & { assignmentId: string | null }) => {
    if (JSON.stringify(selectedOption) !== JSON.stringify(selectedPeriod)) {
      setSelectedPeriod(selectedOption);
      onSelectedPeriodChange?.({
        label: selectedOption.label,
        value: selectedOption.originalPeriodId,
        startDate: selectedOption.startDate,
        endDate: selectedOption.endDate,
        screeningWindowType: selectedOption.screeningWindowType,
        assignmentId: selectedOption.assignmentId,
        assignmentType: selectedOption.assignmentType as AssignmentType
      });
    }
  }, [onSelectedPeriodChange, selectedPeriod]);



  return (
    <div className={`testing-window d-flex ${assessmentModal ? 'flex-row align-items-center' : 'flex-column align-items-start align-self-start mb-3'} gap-2`}>
      <div className="d-flex align-items-center gap-2">
        <span className="testing-window-label">Testing Window</span>
      </div>
      <div>
        {(assessmentModal && (!assessmentWindowStatus?.activeAssessmentWindow || !districtScreeningSetup)) ? (
          <div className="testing-window-placeholder">
            <span>None</span>
          </div>
        ) : (
          <Dropdown
            styles={{ width: '18rem', zIndex: 10, fontSize: '0.8125rem', textOverflow: 'ellipsis', overflow: 'hidden', minHeight: '2.375rem', gap: '0.25rem' }}
            type={DropdownType.PRIMARY}
            placeholder={assessmentWindowStatus?.activeAssessmentWindow ? selectedPeriod?.label || 'None' : 'None'}
            isDisabled={assessmentModal}
            value={selectedPeriod}
            onChange={handleDropdownChange}
            aria-label="Select Testing Window"
            options={options}
          />
        )}
      </div>
    </div>
  );
};

export default TestingWindow;